import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Checkbox from '../../components/common/Checkbox/Checkbox';
import ClearFiltersButton from '../../components/common/ClearFiltersButton/ClearFiltersButton';
import ConfirmModal from '../../components/common/ConfirmModal/ConfirmModal';
import CopyStudentNameButton from '../../components/common/CopyStudentNameButton/CopyStudentNameButton';
import DataTable from '../../components/common/DataTable/DataTable';
import DatePresetFilter from '../../components/common/DatePresetFilter/DatePresetFilter';
import EmptyState from '../../components/common/EmptyState/EmptyState';
import FilterBar from '../../components/common/FilterBar/FilterBar';
import FilterGroup from '../../components/common/FilterGroup/FilterGroup';
import ListingBottomRow from '../../components/common/ListingBottomRow/ListingBottomRow';
import Loading from '../../components/common/Loading/Loading';
import RecordCount from '../../components/common/RecordCount/RecordCount';
import SearchFilterBar from '../../components/common/SearchFilterBar/SearchFilterBar';
import ViewLinkButton from '../../components/common/ViewLinkButton/ViewLinkButton';
import {
  detectDatePreset,
  RECENT_BODY_PHOTOS_PERIOD_PRESETS,
} from '../../constants/datePresets';
import { useRecentBodyPhotoAssessments } from '../../hooks/useRecentBodyPhotoAssessments';
import { queryFilter, useQueryFilters } from '../../hooks/useQueryFilters';
import { formatDate, formatDateTime } from '../../utils/formatting';
import {
  recentBodyPhotoAssessmentRowKey,
  resolveRecentBodyPhotoAssessmentsQuery,
} from '../../utils/recentBodyPhotoAssessments';
import dashboardStyles from './Dashboard.module.css';
import styles from './RecentBodyPhotosPanel.module.css';

const PAGE_SIZE_OPTIONS = [10, 20, 50] as const;
const REVIEW_LEAVES_LIST_MESSAGE =
  'Ao marcar Avaliar, no dia seguinte essa pasta sai da lista.';
const REVIEW_UNMARK_MESSAGE =
  'A pasta volta a aparecer como não avaliada.';

const HEADERS = ['Aluna', 'Professora', 'Quando enviou', '', 'Avaliar'];
const GRID_TEMPLATE = 'minmax(220px, 1.5fr) minmax(140px, 1fr) minmax(160px, 1fr) minmax(120px, auto) 120px';

interface PendingReview {
  studentId: number;
  assessmentDate: string;
  studentName: string;
  reviewed: boolean;
}

const FILTER_SCHEMA = {
  dateStart: queryFilter.string('', { param: 'de', trim: true }),
  dateEnd: queryFilter.string('', { param: 'ate', trim: true }),
  searchName: queryFilter.string(''),
  page: queryFilter.integer(1, { min: 1 }),
  pageSize: queryFilter.integer(20, { allowed: PAGE_SIZE_OPTIONS }),
};

const RecentBodyPhotosPanel: React.FC = () => {
  const navigate = useNavigate();
  const { filters, setFilter, setFilters, resetFilters } = useQueryFilters(
    FILTER_SCHEMA,
    { pageKey: 'page' },
  );

  const resolved = useMemo(
    () => resolveRecentBodyPhotoAssessmentsQuery(
      filters.dateStart,
      filters.dateEnd,
      filters.searchName,
      filters.page,
      filters.pageSize,
    ),
    [
      filters.dateEnd,
      filters.dateStart,
      filters.page,
      filters.pageSize,
      filters.searchName,
    ],
  );
  const query = resolved.ok ? resolved.query : null;

  const { snapshot, loading, error, setReviewed } = useRecentBodyPhotoAssessments(query);
  const [savingKey, setSavingKey] = useState<string | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [pendingReview, setPendingReview] = useState<PendingReview | null>(null);
  const totalCount = snapshot?.totalCount ?? 0;
  const totalPages = Math.max(1, Math.ceil(totalCount / filters.pageSize));
  const page = Math.min(filters.page, totalPages);
  const assessments = snapshot?.assessments ?? [];
  const hasDateFilter = Boolean(filters.dateStart && filters.dateEnd);
  const hasActiveFilters = hasDateFilter || Boolean(filters.searchName.trim());

  const handlePeriodChange = (start: string, end: string) => {
    const detected = detectDatePreset(start, end, RECENT_BODY_PHOTOS_PERIOD_PRESETS);
    if (detected === 'custom' || !detected) {
      setFilters({ dateStart: start, dateEnd: end });
      return;
    }

    setFilters({ dateStart: start, dateEnd: end });
  };

  const persistReview = (studentId: number, assessmentDate: string, reviewed: boolean) => {
    const rowKey = recentBodyPhotoAssessmentRowKey({ studentId, assessmentDate });
    setSavingKey(rowKey);
    setSaveError(null);
    void setReviewed(studentId, assessmentDate, reviewed)
      .catch((caught: unknown) => {
        setSaveError(caught instanceof Error ? caught.message : 'Não foi possível atualizar a avaliação.');
      })
      .finally(() => {
        setSavingKey(null);
      });
  };

  const handleToggleReviewed = (
    studentId: number,
    assessmentDate: string,
    studentName: string,
    currentlyReviewed: boolean,
  ) => {
    if (savingKey != null) {
      return;
    }

    setPendingReview({
      studentId,
      assessmentDate,
      studentName,
      reviewed: !currentlyReviewed,
    });
  };

  const confirmPendingReview = () => {
    if (!pendingReview || savingKey != null) {
      return;
    }

    const next = pendingReview;
    setPendingReview(null);
    persistReview(next.studentId, next.assessmentDate, next.reviewed);
  };

  return (
    <section className={styles.panel}>
      <FilterBar gap="sm" compact pushFirstChild className={dashboardStyles.filtersRow}>
        <RecordCount label="Pastas:" count={resolved.ok ? totalCount : 0} />
        <FilterGroup>
          <SearchFilterBar
            searchValue={filters.searchName}
            onSearchChange={(value) => setFilter('searchName', value)}
            searchPlaceholder="Nome ou professora"
            loading={loading}
          />
        </FilterGroup>
        <FilterGroup>
          <DatePresetFilter
            dateStart={filters.dateStart}
            dateEnd={filters.dateEnd}
            onChange={handlePeriodChange}
            placeholder="Período"
            presetOptions={RECENT_BODY_PHOTOS_PERIOD_PRESETS}
            allowEmpty
          />
        </FilterGroup>
        {hasActiveFilters && (
          <ClearFiltersButton onClick={() => resetFilters()} />
        )}
      </FilterBar>

      {saveError && <p className={styles['save-error']}>{saveError}</p>}

      {!resolved.ok && <EmptyState message={resolved.message} showImage={false} />}
      {resolved.ok && loading && !snapshot && <Loading />}
      {resolved.ok && !loading && error && <EmptyState message={error} showImage={false} />}
      {resolved.ok && !loading && !error && snapshot && totalCount === 0 && (
        <EmptyState
          message={hasActiveFilters
            ? 'Nenhuma pasta encontrada com os filtros selecionados.'
            : 'Nenhuma pasta de fotos recentes para avaliar.'}
          showImage={false}
        />
      )}
      {resolved.ok && !error && snapshot && totalCount > 0 && (
        <>
          <DataTable
            headers={HEADERS}
            gridTemplate={GRID_TEMPLATE}
            lastColumnIsActions={false}
            borderedRows
            boldHeaders
            highlightTerm={filters.searchName}
          >
            {assessments.map((row) => {
              const rowKey = recentBodyPhotoAssessmentRowKey(row);

              return (
                <DataTable.Row key={rowKey}>
                  <div className={styles['student-cell']}>
                    <div className={styles['name-row']}>
                      <button
                        type="button"
                        className={styles['name-link']}
                        onClick={() => navigate(`/alunas/${row.studentId}/editar`)}
                      >
                        {row.studentName}
                      </button>
                      <CopyStudentNameButton studentName={row.studentName} />
                    </div>
                  </div>
                  <div>{row.coachName || 'Sem professora'}</div>
                  <div>{formatDateTime(row.uploadedAt)}</div>
                  <div className={styles['view-photos-cell']}>
                    <ViewLinkButton
                      label="Ver fotos"
                      aria-label={`Ver fotos de ${row.studentName}`}
                      onClick={() => {
                        window.open(
                          `/alunas/${row.studentId}/editar?section=photos`,
                          '_blank',
                          'noopener,noreferrer',
                        );
                      }}
                    />
                  </div>
                  <div className={styles['review-cell']}>
                    <Checkbox
                      variant="classic"
                      compact
                      checked={row.reviewed}
                      disabled={savingKey === rowKey}
                      ariaLabel={row.reviewed
                        ? `Desmarcar avaliação das fotos de ${row.studentName}`
                        : `Avaliar fotos de ${row.studentName}`}
                      onPress={() => handleToggleReviewed(
                        row.studentId,
                        row.assessmentDate,
                        row.studentName,
                        row.reviewed,
                      )}
                    />
                  </div>
                </DataTable.Row>
              );
            })}
          </DataTable>
          <ListingBottomRow
            pageSize={filters.pageSize}
            pageSizeOptions={PAGE_SIZE_OPTIONS}
            onPageSizeChange={(event) => {
              const value = Number.parseInt(event.target.value, 10);
              if (PAGE_SIZE_OPTIONS.some((option) => option === value)) {
                setFilter('pageSize', value);
              }
            }}
            pagination={{
              page,
              pageSize: filters.pageSize,
              totalRecords: totalCount,
              totalPages,
              hasNextPage: page < totalPages,
              hasPreviousPage: page > 1,
            }}
            loading={loading}
            onPageChange={(nextPage) => setFilter('page', nextPage, { resetPage: false })}
          />
          <ConfirmModal
            open={pendingReview !== null}
            title={pendingReview?.reviewed ? 'Avaliar fotos' : 'Desmarcar avaliação'}
            message={pendingReview
              ? pendingReview.reviewed
                ? `Marcar as fotos de ${pendingReview.studentName} (pasta ${formatDate(pendingReview.assessmentDate)}) como avaliadas? ${REVIEW_LEAVES_LIST_MESSAGE}`
                : `Desmarcar a avaliação das fotos de ${pendingReview.studentName} (pasta ${formatDate(pendingReview.assessmentDate)})? ${REVIEW_UNMARK_MESSAGE}`
              : REVIEW_LEAVES_LIST_MESSAGE}
            confirmLabel="Confirmar"
            cancelLabel="Cancelar"
            variant="info"
            icon="photo_library"
            loading={savingKey != null}
            onConfirm={confirmPendingReview}
            onCancel={() => {
              if (savingKey == null) {
                setPendingReview(null);
              }
            }}
          />
        </>
      )}
    </section>
  );
};

export default RecentBodyPhotosPanel;
